// Future Financial Outlook — deterministic, client-side lifetime projection
// engine. Recomputes instantly on every slider change (no network round-trip).

export const MILESTONE_AGES = [25, 30, 35, 40, 45, 50, 60, 65];

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

// Progressive effective tax rate (federal + state blended, simplified).
function effectiveTaxRate(gross) {
  if (gross < 40000) return 0.12;
  if (gross < 90000) return 0.18;
  if (gross < 180000) return 0.24;
  if (gross < 400000) return 0.30;
  return 0.35;
}

// Salary for a given years-of-experience using the 4-point curve.
function salaryAt(yearsExp, s) {
  if (yearsExp < 0) return 0;
  const lerp = (a, b, t) => a + (b - a) * clamp(t, 0, 1);
  if (yearsExp <= 2) return s.salary_entry;
  if (yearsExp <= 7) return lerp(s.salary_entry, s.salary_mid, (yearsExp - 2) / 5);
  if (yearsExp <= 15) return lerp(s.salary_mid, s.salary_senior, (yearsExp - 7) / 8);
  if (yearsExp <= 25) return lerp(s.salary_senior, s.salary_peak, (yearsExp - 15) / 10);
  return s.salary_peak * (1 + (yearsExp - 25) * 0.01); // modest late-career COLA
}

// Standard fixed-payment amortization → yearly {payment, interestPaid, endBalance}.
function amortizeYear(balance, annualRate, monthlyPayment) {
  let bal = balance;
  let interest = 0;
  const r = annualRate / 12;
  for (let m = 0; m < 12 && bal > 0; m++) {
    const i = bal * r;
    interest += i;
    bal = Math.max(0, bal + i - monthlyPayment);
  }
  return { endBalance: bal, interestPaid: interest };
}

function monthlyPaymentFor(principal, annualRate, years) {
  if (principal <= 0) return 0;
  const r = annualRate / 12;
  const n = years * 12;
  if (r === 0) return principal / n;
  return (principal * r) / (1 - Math.pow(1 + r, -n));
}

/**
 * Compute a full lifetime projection for a scenario.
 * Returns { years: [...perYear], milestones: {age:row}, summary: {...} }
 */
export function computeProjection(sc) {
  const col = sc.col_multiplier || 1;
  const currentAge = sc.current_age || 18;
  const startWorkAge = currentAge + (sc.education_years || 0);
  const retirementAge = sc.retirement_age || 65;
  const endAge = 90;

  // Income: the "target income" slider sets mid-career pay; entry/senior/peak
  // keep their original shape (ratios) so the whole curve scales intuitively.
  const baseMid = sc.salary_mid || 60000;
  const target = sc.target_income || baseMid;
  const curve = {
    salary_entry: target * ((sc.salary_entry || 0) / baseMid),
    salary_mid: target,
    salary_senior: target * ((sc.salary_senior || 0) / baseMid),
    salary_peak: target * ((sc.salary_peak || 0) / baseMid),
  };

  // Education financing — finance 85% of the net cost not covered by aid.
  const netEduCost = Math.max(0, (sc.education_cost || 0) - (sc.scholarships || 0) - (sc.grants || 0));
  let loanBalance = netEduCost * 0.85;
  const loanPayment = monthlyPaymentFor(loanBalance, (sc.loan_rate || 0) / 100, sc.loan_term_years || 10);

  // Housing
  const owns = sc.housing === 'own';
  const homePrice = (sc.home_price || 0);
  const downPct = 0.15;
  let mortgageBalance = 0;
  let mortgageStarted = false;
  let homeValue = 0;
  const mortgageRate = 0.065;
  const mortgageTermYears = 30;
  let mortgagePayment = 0;

  const invReturn = (sc.investment_return || 7) / 100;
  const savingsRate = clamp((sc.savings_rate || 0) / 100, 0, 1);
  const investPct = (sc.investment_pct || 0) / 100;
  const employerMatch = (sc.employer_match_pct || 0) / 100;

  let cash = 0;
  let investments = 0;
  let retirement = 0;

  const years = [];
  let totalEarnings = 0;
  let totalTaxes = 0;
  let totalRetireContrib = 0;
  let totalEmployerMatch = 0;
  let totalInterestPaid = 0;

  for (let age = currentAge; age <= endAge; age++) {
    const yearsExp = age - startWorkAge;
    const working = age >= startWorkAge && age < retirementAge;

    let salary = working ? salaryAt(yearsExp, curve) : 0;
    const side = working ? (sc.side_income || 0) : 0;
    const gross = salary + side;
    totalEarnings += gross;

    const tax = gross * effectiveTaxRate(gross);
    totalTaxes += tax;
    const net = gross - tax;

    // Living expenses (scaled by cost of living + family). Before someone starts earning they
    // are not personally carrying their own cost of living — family, aid, or the education cost
    // itself covers it. Charging a 16-year-old a designed adult lifestyle used to manufacture
    // hundreds of thousands in imaginary debt before their first payslip.
    const preWork = age < startWorkAge;
    let living = preWork ? 0 : (sc.monthly_living_cost || 0) * 12;
    if (!preWork) living += (sc.vacation_budget || 0);
    const kids = sc.kids || 0;
    if (!preWork && kids > 0 && age >= startWorkAge + 3 && age < startWorkAge + 3 + 18) {
      living += kids * 15000 * col;
    }

    // Housing
    let housingCost;
    if (owns && age >= (sc.home_purchase_age || 30)) {
      if (!mortgageStarted) {
        const down = homePrice * downPct;
        cash -= down; // may push cash negative if under-saved (a real tradeoff)
        mortgageBalance = homePrice - down;
        homeValue = homePrice;
        mortgagePayment = monthlyPaymentFor(mortgageBalance, mortgageRate, mortgageTermYears);
        mortgageStarted = true;
      }
      const am = amortizeYear(mortgageBalance, mortgageRate, mortgagePayment);
      housingCost = mortgagePayment * 12 + homePrice * 0.015; // + property tax / upkeep
      mortgageBalance = am.endBalance;
      homeValue = homeValue * 1.03; // appreciation
    } else {
      const monthlyRent = (homePrice * 0.005) || (sc.monthly_living_cost || 0) * 0.35;
      housingCost = preWork ? 0 : monthlyRent * 12;
    }

    // Student-loan payment is a real cash outflow while a balance remains.
    let loanPay = 0;
    if (loanBalance > 0 && age >= startWorkAge) {
      const am = amortizeYear(loanBalance, (sc.loan_rate || 0) / 100, loanPayment);
      loanPay = (loanBalance + am.interestPaid) - am.endBalance;
      totalInterestPaid += am.interestPaid;
      loanBalance = am.endBalance;
    }

    // Nobody spends money they don't have year after year. If the designed lifestyle costs more
    // than this career pays, we spend what's affordable and report the shortfall as a lifestyle
    // gap — the honest answer — instead of compounding it into debt that never existed.
    const wanted = living + housingCost;
    let expenses = wanted;
    let lifestyleGap = 0;
    if (working) {
      const affordable = Math.max(0, net - loanPay);
      if (wanted > affordable) {
        lifestyleGap = wanted - affordable;
        expenses = affordable;
      }
    }
    const mandatory = expenses + loanPay;
    const leftover = net - mandatory;

    // Grow existing balances one year.
    retirement = retirement * (1 + invReturn);
    investments = investments * (1 + invReturn);

    if (working) {
      if (leftover > 0) {
        // Save up to the chosen rate, capped by what's actually left over.
        const intendedSavings = net * savingsRate;
        const actualSavings = Math.min(intendedSavings, leftover);
        const retireContrib = Math.min(actualSavings, gross * 0.15);
        const match = Math.min(gross * employerMatch, retireContrib);
        const rest = actualSavings - retireContrib;
        retirement += retireContrib + match;
        investments += rest * investPct;
        cash += rest * (1 - investPct);
        totalRetireContrib += retireContrib;
        totalEmployerMatch += match;
      } else {
        cash += leftover; // negative cash flow → dips into (or below) cash
      }
    } else {
      // Retired: cover the shortfall by drawing down investments, then retirement, then cash.
      let shortfall = mandatory - net;
      if (shortfall > 0) {
        const fromInv = Math.min(shortfall, investments); investments -= fromInv; shortfall -= fromInv;
        const fromRet = Math.min(shortfall, retirement); retirement -= fromRet; shortfall -= fromRet;
        if (shortfall > 0) cash -= shortfall;
      }
    }

    const homeEquity = owns && mortgageStarted ? Math.max(0, homeValue - mortgageBalance) : 0;
    // Net worth: mortgage is already netted inside home equity — do NOT subtract it again.
    const netWorth = cash + investments + retirement + homeEquity - loanBalance;
    const disposable = leftover;
    const desiredLifestyleCost = ((sc.monthly_living_cost || 0) * 12) + (sc.vacation_budget || 0) + (owns ? homePrice * 0.05 : 0);
    const affordability = clamp(Math.round((net / Math.max(1, desiredLifestyleCost)) * 55), 0, 100);

    years.push({
      age,
      salary: Math.round(salary),
      gross: Math.round(gross),
      net: Math.round(net),
      expenses: Math.round(expenses),
      savings: Math.round(cash),
      investments: Math.round(investments),
      retirement: Math.round(retirement),
      homeEquity: Math.round(homeEquity),
      studentLoan: Math.round(loanBalance),
      lifestyleGap: Math.round(lifestyleGap),
      inEducation: preWork,
      mortgage: Math.round(mortgageBalance),
      netWorth: Math.round(netWorth),
      disposable: Math.round(disposable),
      affordability,
    });
  }

  const milestones = {};
  MILESTONE_AGES.forEach((a) => {
    milestones[a] = years.find((y) => y.age === a) || years[years.length - 1];
  });

  const atRetire = years.find((y) => y.age === retirementAge) || years[years.length - 1];
  const fiRow = years.find((y) => y.netWorth >= (sc.monthly_living_cost || 0) * 12 * 25);

  const summary = {
    lifetimeEarnings: Math.round(totalEarnings),
    totalTaxes: Math.round(totalTaxes),
    retirementContributions: Math.round(totalRetireContrib),
    employerMatch: Math.round(totalEmployerMatch),
    netWorthAtRetirement: Math.round(atRetire.netWorth),
    studentLoanInterest: Math.round(totalInterestPaid),
    financialIndependenceAge: fiRow ? fiRow.age : null,
    peakNetWorth: Math.round(Math.max(...years.map((y) => y.netWorth))),
    startWorkAge,
    retirementAge,
  };

  return { years, milestones, summary };
}

// Rule-based contextual "financial intelligence" insights (no LLM, instant).
export function contextualInsights(sc, proj) {
  const out = [];
  const mid = sc.salary_mid || 0;
  const monthlyHousing = sc.housing === 'own'
    ? (sc.home_price || 0) * 0.005
    : (sc.home_price || 0) * 0.005 || (sc.monthly_living_cost || 0) * 0.35;
  const housingAnnual = monthlyHousing * 12;
  const housingPct = mid > 0 ? Math.round((housingAnnual / mid) * 100) : 0;
  if (housingPct > 28) {
    out.push({
      tone: 'warn',
      text: `At a ${sc.housing === 'own' ? `$${Math.round(sc.home_price / 1000)}K home` : 'this rent'} on a ~$${Math.round(mid / 1000)}K salary, housing is about ${housingPct}% of income. Planners often suggest staying near 28%.`,
    });
  } else if (housingPct > 0) {
    out.push({ tone: 'good', text: `Your housing is about ${housingPct}% of income — comfortably within the recommended 28%.` });
  }

  // Early vs late investing (compound interest illustration)
  const yrsToRetire = (sc.retirement_age || 65) - ((sc.current_age || 18) + (sc.education_years || 0));
  if (yrsToRetire > 20) {
    const monthly = 300;
    const r = (sc.investment_return || 7) / 100 / 12;
    const fv = (n) => monthly * ((Math.pow(1 + r, n) - 1) / r);
    const early = fv(yrsToRetire * 12);
    const late = fv((yrsToRetire - 10) * 12);
    const delta = Math.round((early - late) / 1000) * 1000;
    out.push({
      tone: 'good',
      text: `Investing $300/mo starting now instead of 10 years later adds about $${delta.toLocaleString()} by retirement — that's compound growth working for you.`,
    });
  }

  // Savings rate nudge
  if ((sc.savings_rate || 0) < 15) {
    out.push({ tone: 'warn', text: `Your savings rate is ${sc.savings_rate || 0}%. Bumping toward 15–20% meaningfully accelerates your net worth.` });
  }

  // Target income gap
  if (sc.target_income && sc.salary_peak && sc.target_income > sc.salary_peak * 1.15) {
    out.push({
      tone: 'warn',
      text: `Your target of $${Math.round(sc.target_income / 1000)}K/yr is above this career's typical peak (~$${Math.round(sc.salary_peak / 1000)}K). Reaching it likely needs equity, a business, or a specialized/leadership track.`,
    });
  }

  return out;
}

export const fmtMoney = (n) => {
  if (n == null || isNaN(n)) return '$0';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${n < 0 ? '-' : ''}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${n < 0 ? '-' : ''}$${Math.round(abs / 1000)}K`;
  return `${n < 0 ? '-' : ''}$${Math.round(abs)}`;
};

export const fmtFull = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
